// /orders/mac/new (plan omni-rick-round 3.1 and 7C, mock M6 mac-orders.html): a MAC (move, add, change)
// for an existing customer starts from the support ticket where the tech recorded the work. Phoneware picks the
// customer, then one of its recent or open tickets; Omni reads the ticket's charges matched to the catalog
// and names anything it can't match. They keep or leave out each charge, add the monthly services, pick No
// contract or Coterminous and mark it sold: Omni saves a change quote with its source ticket
// (POST /api/quotes) and marks it sold (POST /api/quotes/:id/sold), which creates the order with no
// customer signature and no new Autotask ticket. The order then waits for billing approval like any other.
// Support, Sales and Super admins at Phoneware (mac.start): anyone else who opens the address gets the server's refusal
// and nothing else.
import React, { useEffect, useRef, useState } from "react";
import { formatCents, isBillable, type CatalogProductV2, type CustomerSummary, type Dealer, type MacLineCandidate, type Quote } from "@phoneware/lens-domain";
import { Icon } from "../icons.js";
import {
  MAC_TERMS,
  MAC_TERM_WORD,
  customerReady,
  lineBillable,
  lineProduct,
  lineState,
  macProblems,
  macQuoteInput,
  macTotals,
  onebillAccount,
  reasonOf,
  searchCatalog,
  searchCustomers,
  ticketStatusWord,
  TICKET_COMPLETE,
  type MacLine,
  type MacService,
  type MacTerm,
  type SupportTicket,
} from "../mac-order.js";
import { Link, navigate } from "../router.js";
import { useLoad, useSession } from "../session.js";
import { BILLING, errorText, plural } from "../format.js";
import { Callout, Chip, Crumb, Head, Sheet, useTitle, useToast } from "../ui.js";
import { AccountNumber } from "./customer-bits.js";

/** How many rows each list shows before asking for more letters. */
const CUSTOMER_ROWS = 8;
const SERVICE_ROWS = 12;
const PICK_ROWS = 20;

/** How each matched charge found its product (MacLineCandidate.why). */
const MATCHED_BY: Record<NonNullable<MacLineCandidate["why"]>, string> = {
  product_id: "same product as the ticket",
  exact_name: "matched by name",
  product_key: "matched by name",
};

/** "Oct 16", or "Jun 12, 2025" when it isn't this year. */
function shortDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("en-US", sameYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" });
}

export function MacOrder(): React.ReactElement {
  const { can } = useSession();
  useTitle("New MAC order");
  return can("mac.start") ? <MacOrderForm /> : <OpsOnly />;
}

/** What a dealer sees at this address: the server answers every MAC call with 403 and these words. */
function OpsOnly(): React.ReactElement {
  return (
    <>
      <Crumb to="/orders">Orders</Crumb>
      <div className="card notyours" data-testid="mac-ops-only">
        <span className="ny-ic">
          <Icon name="lock" size={20} />
        </span>
        <div>
          <h1>You don&rsquo;t have access to this.</h1>
          <p>MAC orders are started by Support, Sales or a Super admin at Phoneware.</p>
          <Link className="btn" to="/orders">
            Back to your orders
          </Link>
        </div>
      </div>
    </>
  );
}

function StepCard({ n, title, state, right, testId, children }: { n: number; title: string; state: "active" | "done" | "locked"; right?: React.ReactNode; testId: string; children?: React.ReactNode }): React.ReactElement {
  return (
    <section className={`card stepc ${state}`} data-testid={testId} data-state={state}>
      <div className="card-h">
        <span className="sn">{state === "done" ? <Icon name="check" size={12} /> : n}</span>
        <h2>{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

/** A list row that picks something: a click, or Enter or Space from the keyboard. */
function PickRow({ testId, onPick, children }: { testId: string; onPick: () => void; children: React.ReactNode }): React.ReactElement {
  return (
    <li>
      <div
        className="row"
        role="button"
        tabIndex={0}
        data-testid={testId}
        onClick={onPick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onPick();
          }
        }}
      >
        {children}
      </div>
    </li>
  );
}

/** The customer's OneBill state as the search list tags it. */
function OneBillChip({ customer }: { customer: CustomerSummary }): React.ReactElement {
  const account = onebillAccount(customer);
  if (account) {
    return (
      <Chip kind="ok" icon="link">
        OneBill {account}
      </Chip>
    );
  }
  return customer.billing.state === "unreviewed" ? (
    <Chip kind="warn" icon="triangle-alert">
      OneBill not reviewed
    </Chip>
  ) : (
    <Chip kind="quiet">No OneBill account</Chip>
  );
}

function MacOrderForm(): React.ReactElement {
  const { me, api } = useSession();
  const toast = useToast();
  const customers = useLoad<CustomerSummary[]>(() => api.customers(), []);
  const dealers = useLoad<Dealer[]>(() => api.dealers(), []);
  const catalog = useLoad<CatalogProductV2[]>(() => api.fullCatalog(), []);

  const [q, setQ] = useState("");
  const [customerId, setCustomerId] = useState<number | null>(null);
  const customer = customers.data?.find((c) => c.autotaskCompanyId === customerId) ?? null;
  const ready = customerReady(customer);
  // Each answer carries whose it is, so a list read for the last pick never shows under the next one.
  const tickets = useLoad<{ customerId: number; list: SupportTicket[] } | null>(
    () => (customerId !== null && ready ? api.customerTickets(customerId).then((list) => ({ customerId, list })) : Promise.resolve(null)),
    [customerId, ready],
  );

  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const charges = useLoad<{ ticketId: number; candidates: MacLineCandidate[] } | null>(
    () => (ticket ? api.ticketLines(ticket.id).then((candidates) => ({ ticketId: ticket.id, candidates })) : Promise.resolve(null)),
    [ticket?.id],
  );
  const [lines, setLines] = useState<MacLine[]>([]);
  useEffect(() => {
    setLines((charges.data?.candidates ?? []).map((candidate) => ({ candidate, picked: null, out: false })));
  }, [charges.data]);

  const [services, setServices] = useState<MacService[]>([]);
  const [svcQ, setSvcQ] = useState("");
  const [term, setTerm] = useState<MacTerm>("month_to_month");
  const [refused, setRefused] = useState<string[] | null>(null);
  const [flash, setFlash] = useState<number[]>([]);
  const [pickFor, setPickFor] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  // The change quote the first Mark sold saved. A retry for the same customer after a refusal updates it rather than saving a second one.
  const [quote, setQuote] = useState<Quote | null>(null);

  useEffect(() => {
    if (!flash.length) return;
    const t = window.setTimeout(() => setFlash([]), 1300);
    return () => window.clearTimeout(t);
  }, [flash]);

  const dealerName = (id: string) => dealers.data?.find((d) => d.id === id)?.name ?? "";
  const account = onebillAccount(customer);
  const linesReady = !!ticket && !charges.loading && !charges.error && charges.data?.ticketId === ticket.id;
  // Nothing counts while the ticket's steps are locked, as after the server finds the customer's OneBill account unlinked.
  const totals = ready && ticket ? macTotals(lines, services) : { monthly: 0, oneTime: 0 };
  const kept = lines.filter((l) => lineState(l) === "ok").length;
  const leftOut = lines.filter((l) => l.out).length;
  const toSort = lines.filter((l) => lineState(l) === "bad" || lineState(l) === "open").length;
  const today = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  function setLine(i: number, next: Partial<MacLine>): void {
    setLines((all) => all.map((l, j) => (j === i ? { ...l, ...next } : l)));
    setRefused(null);
  }

  function pickCustomer(id: number): void {
    setCustomerId(id);
    setTicket(null);
    setRefused(null);
  }

  function changeCustomer(): void {
    setCustomerId(null);
    setTicket(null);
    setServices([]);
    setRefused(null);
    // Ops may have just linked a OneBill account in another tab: the list reads each customer's state again.
    customers.reload();
  }

  function pickTicket(t: SupportTicket): void {
    setTicket(t);
    setRefused(null);
  }

  function changeTicket(): void {
    setTicket(null);
    setRefused(null);
  }

  function addService(product: CatalogProductV2): void {
    setServices((all) => (all.some((s) => s.product.id === product.id) ? all : [...all, { product, qty: 1 }]));
    setRefused(null);
  }

  function serviceQty(id: string, by: number): void {
    setServices((all) => all.map((s) => (s.product.id === id ? { ...s, qty: Math.max(1, s.qty + by) } : s)));
    setRefused(null);
  }

  function markSold(): void {
    const problems = macProblems(lines, services);
    if (problems.length) {
      setRefused(problems.map((p) => p.text));
      setFlash(problems.flatMap((p) => (p.line === null ? [] : [p.line])));
      return;
    }
    setRefused(null);
    setConfirming(true);
  }

  async function confirmSold(): Promise<void> {
    if (!customer || !ticket) return;
    setBusy(true);
    try {
      const input = macQuoteInput(customer.autotaskCompanyId, ticket, term, lines, services);
      const saved = quote && quote.customerId === customer.autotaskCompanyId ? await api.updateQuote(quote.id, input) : await api.createQuote(input);
      setQuote(saved);
      const order = await api.markSold(saved.id, { expectedUpdatedAt: saved.updatedAt });
      toast(`Marked sold. ${order.number} waits for billing approval.`);
      navigate(`/orders/${encodeURIComponent(order.id)}`);
    } catch (err) {
      setConfirming(false);
      setBusy(false);
      setRefused([errorText(err)]);
      // What the server refused may be the customer's OneBill state changing underneath: read it again.
      customers.reload();
    }
  }

  // ---------------------------------------------------------------- step 1: the customer
  let customerStep: React.ReactElement;
  if (!customer) {
    const found = customers.data ? searchCustomers(customers.data, q) : [];
    customerStep = (
      <StepCard n={1} title="Customer" state="active" testId="mac-step-customer">
        <label className="search">
          <Icon name="search" size={15} />
          <span className="sr">Search customers</span>
          <input className="input" data-testid="mac-customer-q" autoComplete="off" placeholder="Search by name or account number" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        {customers.error ? (
          <Callout kind="stop" icon="triangle-alert">
            {customers.error.message}
          </Callout>
        ) : !customers.data ? (
          <div className="empty">Loading customers.</div>
        ) : !found.length ? (
          <div className="empty" data-testid="mac-customer-none">
            No customer matches &ldquo;{q}&rdquo;.
          </div>
        ) : (
          <>
            <ul className="rows">
              {found.slice(0, CUSTOMER_ROWS).map((c) => (
                <PickRow key={c.autotaskCompanyId} testId={`mac-customer-${c.autotaskCompanyId}`} onPick={() => pickCustomer(c.autotaskCompanyId)}>
                  <span className="row-ic">
                    <Icon name="building-2" size={16} />
                  </span>
                  <div className="row-main">
                    <b>{c.name}</b>
                    <span className="row-m num-src">
                      <AccountNumber number={c.accountNumber} />
                      {[dealerName(c.dealerId), c.address ? `${c.address.city}, ${c.address.state}` : null].filter(Boolean).map((part) => ` \u00b7 ${part}`)}
                    </span>
                  </div>
                  <div className="row-end">
                    <OneBillChip customer={c} />
                  </div>
                </PickRow>
              ))}
            </ul>
            {found.length > CUSTOMER_ROWS ? <p className="more">{found.length - CUSTOMER_ROWS} more. Keep typing to narrow it.</p> : null}
          </>
        )}
      </StepCard>
    );
  } else {
    const picked = (
      <div className="picked" data-testid="mac-customer-picked">
        <span className="row-ic">
          <Icon name="building-2" size={16} />
        </span>
        <div className="row-main">
          <b>{customer.name}</b>
          <span className="row-m num-src">
            <AccountNumber number={customer.accountNumber} />
            {[dealerName(customer.dealerId), account ? `OneBill account ${account}` : customer.billing.state === "unreviewed" ? "OneBill account not reviewed" : "No OneBill account linked"]
              .filter(Boolean)
              .map((part) => ` \u00b7 ${part}`)}
          </span>
        </div>
      </div>
    );
    const change = (
      <button className="link" type="button" data-testid="mac-customer-change" onClick={changeCustomer}>
        Change
      </button>
    );
    customerStep = ready ? (
      <StepCard n={1} title="Customer" state="done" right={change} testId="mac-step-customer">
        {picked}
      </StepCard>
    ) : (
      <StepCard n={1} title="Customer" state="active" right={change} testId="mac-step-customer">
        {picked}
        <div className="callout stop" role="alert" data-testid="mac-customer-refused">
          <Icon name="ban" size={15} />
          <div>
            <b>Link this customer&rsquo;s OneBill account first</b>A MAC order adds to the customer&rsquo;s OneBill account. {customer.name} has none linked yet.
          </div>
        </div>
        <div className="btnrow">
          <Link className="btn small" to={`/customers/${customer.autotaskCompanyId}?billing=review`} data-testid="mac-customer-open">
            <Icon name="link" size={13} />
            Open the customer
          </Link>
          <button className="btn small" type="button" onClick={changeCustomer}>
            Pick another customer
          </button>
        </div>
      </StepCard>
    );
  }

  // ---------------------------------------------------------------- step 2: the support ticket
  let ticketStep: React.ReactElement;
  if (!ready || !customer) {
    ticketStep = <StepCard n={2} title="Support ticket" state="locked" right={<span className="lockwhy">{customer ? "Needs a OneBill account" : "Pick the customer first"}</span>} testId="mac-step-ticket" />;
  } else if (ticket) {
    // As the mock reads it: "Level II Support · Add · Waiting Billing · 3 charges", with the created date when Autotask names no queue or issue type.
    const meta = [
      ticket.queueName,
      ticket.issueTypeName,
      ticket.queueName || ticket.issueTypeName ? null : `created ${shortDate(ticket.createDate)}`,
      ticketStatusWord(ticket),
      linesReady ? plural(lines.length, "charge") : ticket.chargeCount === undefined || ticket.chargeCount === null ? null : plural(ticket.chargeCount, "charge"),
    ]
      .filter(Boolean)
      .join(" \u00b7 ");
    ticketStep = (
      <StepCard
        n={2}
        title="Support ticket"
        state="done"
        right={
          <button className="link" type="button" data-testid="mac-ticket-change" onClick={changeTicket}>
            Change
          </button>
        }
        testId="mac-step-ticket"
      >
        <div className="picked" data-testid="mac-ticket-picked">
          <span className="row-ic">
            <Icon name="ticket" size={16} />
          </span>
          <div className="row-main">
            <span className="row-t">
              {ticket.ticketNumber} <span className="lite">{ticket.title}</span>
            </span>
            <span className="row-m">{meta}</span>
          </div>
        </div>
      </StepCard>
    );
  } else {
    const list = tickets.data?.customerId === customer.autotaskCompanyId ? tickets.data.list : null;
    ticketStep = (
      <StepCard n={2} title="Support ticket" state="active" testId="mac-step-ticket">
        {tickets.error ? (
          <>
            <Callout kind="stop" icon="triangle-alert">
              {tickets.error.message}
            </Callout>
            <div className="btnrow">
              <button className="btn small" type="button" onClick={tickets.reload}>
                <Icon name="refresh-cw" size={13} />
                Try again
              </button>
            </div>
          </>
        ) : tickets.loading || !list ? (
          <div className="empty">Reading {customer.name}&rsquo;s tickets.</div>
        ) : !list.length ? (
          <div className="empty tk-empty" data-testid="mac-tickets-none">
            <Icon name="ticket" size={20} />
            <div>
              <b>No tickets in the last 90 days</b>A MAC order starts from a support ticket for {customer.name}.
            </div>
          </div>
        ) : (
          <>
            <p className="listnote">{customer.name}&rsquo;s tickets created in the last 90 days or not Complete, newest first.</p>
            <ul className="rows" data-testid="mac-tickets">
              {list.map((t) => (
                <PickRow key={t.id} testId={`mac-ticket-${t.id}`} onPick={() => pickTicket(t)}>
                  <span className="row-ic">
                    <Icon name="ticket" size={16} />
                  </span>
                  <div className="row-main">
                    <span className="row-t">
                      {t.ticketNumber} <span className="lite">{t.title}</span>
                    </span>
                    <span className="row-m">{[t.queueName, t.issueTypeName, `created ${shortDate(t.createDate)}`].filter(Boolean).join(" \u00b7 ")}</span>
                  </div>
                  <div className="row-end">
                    <Chip kind={t.status === TICKET_COMPLETE ? "quiet" : ""} status={String(t.status)}>
                      {ticketStatusWord(t)}
                    </Chip>
                    {t.chargeCount === undefined || t.chargeCount === null ? null : (
                      <span className="cnt">
                        <b>{t.chargeCount}</b> {t.chargeCount === 1 ? "charge" : "charges"}
                      </span>
                    )}
                  </div>
                </PickRow>
              ))}
            </ul>
          </>
        )}
      </StepCard>
    );
  }

  // ---------------------------------------------------------------- step 3: the lines
  let linesStep: React.ReactElement;
  if (!ticket || !ready) {
    linesStep = <StepCard n={3} title="Lines" state="locked" right={<span className="lockwhy">Pick the ticket first</span>} testId="mac-step-lines" />;
  } else {
    const svcFound = catalog.data ? searchCatalog(catalog.data, svcQ, true) : [];
    linesStep = (
      <StepCard n={3} title="Lines" state="active" testId="mac-step-lines">
        <div className="src-h">
          <h3>Hardware from {ticket.ticketNumber}</h3>
          {linesReady ? <span className="note">{plural(lines.length, "charge")} on the ticket</span> : null}
        </div>
        {charges.error ? (
          <>
            <Callout kind="stop" icon="triangle-alert">
              {charges.error.message}
            </Callout>
            <div className="btnrow">
              <button className="btn small" type="button" onClick={charges.reload}>
                <Icon name="refresh-cw" size={13} />
                Try again
              </button>
            </div>
          </>
        ) : !linesReady ? (
          <div className="empty">Reading the ticket&rsquo;s charges.</div>
        ) : !lines.length ? (
          <Callout icon="info">No charges on this ticket. Add the monthly services below.</Callout>
        ) : (
          <ul className="ml-list" data-testid="mac-lines">
            {lines.map((l, i) => (
              <LineRow
                key={l.candidate.chargeId}
                line={l}
                index={i}
                flash={flash.includes(i)}
                me={me.user.name}
                onOut={(out) => setLine(i, { out })}
                onPick={() => setPickFor(i)}
              />
            ))}
          </ul>
        )}

        <div className="src-h svc-h">
          <h3>Monthly services</h3>
          <span className="note">You add these</span>
        </div>
        <Callout icon="info">Ticket charges are hardware, never monthly services. Pick the monthly services here.</Callout>
        {services.length ? (
          <ul className="svc-list" data-testid="mac-services">
            {services.map((s) => (
              <li className="svc" key={s.product.id} data-testid={`mac-service-${s.product.id}`}>
                <div className="row-main">
                  <b>{s.product.name}</b>
                  <span className="row-m">
                    {formatCents(s.product.listPrice)} a month &middot; added by {me.user.name}
                  </span>
                </div>
                <span className="mac-qty">
                  <button type="button" aria-label="One fewer" data-testid={`mac-service-less-${s.product.id}`} disabled={s.qty <= 1} onClick={() => serviceQty(s.product.id, -1)}>
                    <Icon name="minus" size={13} />
                  </button>
                  <output aria-label="Quantity" data-testid={`mac-service-qty-${s.product.id}`}>
                    {s.qty}
                  </output>
                  <button type="button" aria-label="One more" data-testid={`mac-service-more-${s.product.id}`} onClick={() => serviceQty(s.product.id, 1)}>
                    <Icon name="plus" size={13} />
                  </button>
                </span>
                <span className="amount">
                  {formatCents(s.product.listPrice * s.qty)}
                  <small>a month</small>
                </span>
                <button
                  className="iconbtn"
                  type="button"
                  aria-label={`Remove ${s.product.name}`}
                  data-testid={`mac-service-remove-${s.product.id}`}
                  onClick={() => {
                    setServices((all) => all.filter((x) => x.product.id !== s.product.id));
                    setRefused(null);
                  }}
                >
                  <Icon name="x" size={15} />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <label className="search">
          <Icon name="search" size={15} />
          <span className="sr">Search monthly services</span>
          <input className="input" data-testid="mac-service-q" autoComplete="off" placeholder="Search the catalog for monthly services" value={svcQ} onChange={(e) => setSvcQ(e.target.value)} />
        </label>
        {svcQ.trim() ? (
          <div className="res" data-testid="mac-service-results">
            {catalog.error ? (
              <div className="empty">{catalog.error.message}</div>
            ) : !catalog.data ? (
              <div className="empty">Loading the catalog.</div>
            ) : !svcFound.length ? (
              <div className="empty">No monthly service matches &ldquo;{svcQ.trim()}&rdquo;.</div>
            ) : (
              <ul className="rows">
                {svcFound.slice(0, SERVICE_ROWS).map((p) => {
                  const billable = isBillable(p);
                  const added = services.some((s) => s.product.id === p.id);
                  return (
                    <li key={p.id}>
                      <div className="row" aria-disabled={billable.ok ? undefined : "true"} data-testid={`mac-service-option-${p.id}`}>
                        <div className="row-main">
                          <span className={billable.ok ? "row-t" : "row-t lite muted"}>{p.name}</span>
                          <span className="row-m">
                            {formatCents(p.listPrice)} a month &middot; {p.category}
                          </span>
                        </div>
                        <div className="row-end">
                          {!billable.ok ? (
                            <span className="why-off">{billable.reason}</span>
                          ) : added ? (
                            <Chip kind="ok" icon="check">
                              Added
                            </Chip>
                          ) : (
                            <button className="btn small" type="button" data-testid={`mac-service-add-${p.id}`} onClick={() => addService(p)}>
                              <Icon name="plus" size={13} />
                              Add
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {svcFound.length > SERVICE_ROWS ? <div className="res-foot">{svcFound.length - SERVICE_ROWS} more. Keep typing to narrow it.</div> : null}
          </div>
        ) : null}
      </StepCard>
    );
  }

  // ---------------------------------------------------------------- step 4: term and start
  const termStep =
    !ticket || !ready ? (
      <StepCard n={4} title="Term and start" state="locked" right={<span className="lockwhy">After the lines</span>} testId="mac-step-term" />
    ) : (
      <StepCard n={4} title="Term and start" state="active" testId="mac-step-term">
        <div className="mac-term">
          <div className="field">
            <span className="label" id="mac-term-label">
              Term
            </span>
            <div className="seg" role="group" aria-labelledby="mac-term-label">
              {MAC_TERMS.map((t) => (
                <button
                  key={t}
                  type="button"
                  aria-pressed={term === t}
                  data-testid={`mac-term-${t}`}
                  onClick={() => {
                    setTerm(t);
                    setRefused(null);
                  }}
                >
                  {MAC_TERM_WORD[t]}
                </button>
              ))}
            </div>
          </div>
          <div className="field">
            <span className="label">Start date</span>
            <div className="datebox">
              <Icon name="calendar" size={15} />
              Today, {today}
            </div>
          </div>
        </div>
      </StepCard>
    );

  // ---------------------------------------------------------------- the summary
  const muted = (text: string) => <span className="muted">{text}</span>;
  const summary = (
    <section className="card mac-sum" data-testid="mac-summary">
      <div className="card-h">
        <h2>This MAC order</h2>
      </div>
      <dl>
        <div>
          <dt>Customer</dt>
          <dd>{customer ? customer.name : muted("Not picked")}</dd>
        </div>
        <div>
          <dt>OneBill account</dt>
          <dd data-testid="mac-summary-account">{account ?? muted(customer ? "None linked" : "Not yet")}</dd>
        </div>
        <div>
          <dt>Source ticket</dt>
          <dd data-testid="mac-summary-ticket">{ticket && ready ? ticket.ticketNumber : muted("Not picked")}</dd>
        </div>
        <div>
          <dt>Term</dt>
          <dd data-testid="mac-summary-term">{ticket && ready ? `${MAC_TERM_WORD[term]}, starts today` : muted("Not yet")}</dd>
        </div>
      </dl>
      <div className="tot">
        <span>Monthly</span>
        <b data-testid="mac-total-monthly">{formatCents(totals.monthly)}</b>
        <span>One-time</span>
        <b data-testid="mac-total-one-time">{formatCents(totals.oneTime)}</b>
      </div>
      {ticket && ready ? (
        <div className="split" data-testid="mac-summary-split">
          <span>
            <Icon name="ticket" size={12} />
            {plural(kept, "line")} from the ticket
          </span>
          <span>
            <Icon name="plus" size={12} />
            {plural(services.length, "monthly service")} added
          </span>
          {toSort ? (
            <span>
              <Icon name="triangle-alert" size={12} />
              {toSort} to sort out
            </span>
          ) : null}
          {leftOut ? (
            <span>
              <Icon name="x" size={12} />
              {leftOut} left out
            </span>
          ) : null}
        </div>
      ) : null}
      {refused ? (
        <div className="callout stop" role="alert" data-testid="mac-refusal">
          <Icon name="ban" size={15} />
          <div>
            <b>Can&rsquo;t mark it sold</b>
            <ul>
              {refused.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
      <button className="btn primary wide" type="button" data-testid="mac-sold" disabled={!ready || !ticket || !linesReady || busy} onClick={markSold}>
        <Icon name="badge-check" size={15} />
        Mark sold
      </button>
    </section>
  );

  return (
    <div className="mac-page">
      <Crumb to="/orders">Orders</Crumb>
      <Head
        title="New MAC order"
        sub={"Pick the customer, then the support ticket. Omni pulls the ticket\u2019s hardware charges and names anything it can\u2019t match. You add the monthly services, pick the term and mark it sold."}
        actions={
          <Chip kind="quiet" icon="lock">
            Phoneware only
          </Chip>
        }
      />
      <div className="mac-grid">
        <div className="stack">
          {customerStep}
          {ticketStep}
          {linesStep}
          {termStep}
        </div>
        <aside className="mac-side">{summary}</aside>
      </div>
      {pickFor !== null && lines[pickFor] && ticket ? (
        <PickSheet
          line={lines[pickFor]}
          ticket={ticket}
          catalog={catalog.data}
          catalogError={catalog.error}
          onPick={(product) => {
            setLine(pickFor, { picked: product, out: false });
            setPickFor(null);
            toast(`${lines[pickFor].candidate.chargeName} now bills as ${product.name}.`);
          }}
          onLeaveOut={() => {
            setLine(pickFor, { out: true });
            setPickFor(null);
          }}
          onClose={() => setPickFor(null)}
        />
      ) : null}
      {confirming && customer && ticket ? (
        <ConfirmSold
          customerName={customer.name}
          account={account ?? ""}
          ticketNumber={ticket.ticketNumber}
          kept={kept}
          services={services.length}
          term={MAC_TERM_WORD[term]}
          today={today}
          monthly={formatCents(totals.monthly)}
          oneTime={formatCents(totals.oneTime)}
          busy={busy}
          onConfirm={() => void confirmSold()}
          onClose={() => setConfirming(false)}
        />
      ) : null}
    </div>
  );
}

/** One ticket charge: what the ticket says on the left, what Omni will bill on the right. */
function LineRow({ line, index, flash, me, onOut, onPick }: { line: MacLine; index: number; flash: boolean; me: string; onOut: (out: boolean) => void; onPick: () => void }): React.ReactElement {
  const c = line.candidate;
  const state = lineState(line);
  const product = lineProduct(line);
  const cls = ["ml", state === "ok" ? "" : state, flash ? "flash" : ""].filter(Boolean).join(" ");
  const putBack = (
    <button className="btn small" type="button" data-testid={`mac-line-back-${index}`} onClick={() => onOut(false)}>
      <Icon name="undo-2" size={13} />
      Put back
    </button>
  );
  const leaveOut = (
    <button className="btn small" type="button" data-testid={`mac-line-out-${index}`} onClick={() => onOut(true)}>
      Leave out
    </button>
  );
  return (
    <li className={cls} data-testid={`mac-line-${index}`} data-state={state}>
      <div className="stub">
        <span className="lab">On the ticket</span>
        <b>{c.chargeName}</b>
        <span className="row-m">
          Qty {c.qty} &middot; {formatCents(c.chargeUnitPriceCents)} each
        </span>
        {c.quantityValid ? null : <span className="why-line warn">{c.quantityError}</span>}
      </div>
      <span className="to">
        <Icon name="arrow-right" size={15} />
      </span>
      <div className="dst">
        <div className="dst-main">
          <span className="lab">In Omni</span>
          {product ? (
            <>
              <b>{product.name}</b>
              <span className="row-m">
                {[product.category, product.billing ? BILLING[product.billing] : null, line.picked ? `picked by ${me}` : c.why ? MATCHED_BY[c.why] : null].filter(Boolean).join(" \u00b7 ")}
              </span>
              {state === "bad" ? <span className="why-line stop">{reasonOf(lineBillable(line))}</span> : null}
            </>
          ) : (
            <>
              <b className="none">Not in the catalog</b>
              <span className="row-m">No catalog product matches &ldquo;{c.chargeName}&rdquo;.</span>
            </>
          )}
          <div className="acts">
            {state === "out" ? (
              putBack
            ) : state === "open" ? (
              <>
                <button className="btn small" type="button" data-testid={`mac-line-pick-${index}`} onClick={onPick}>
                  <Icon name="search" size={13} />
                  Pick a product
                </button>
                {leaveOut}
              </>
            ) : state === "bad" ? (
              <>
                {leaveOut}
                <Link className="btn small" to="/catalog">
                  <Icon name="book-open" size={13} />
                  Open in Catalog
                </Link>
              </>
            ) : (
              leaveOut
            )}
          </div>
        </div>
        <div className="dst-end">
          {product ? (
            <span className="price">
              {formatCents(product.listPrice)}
              <small>list, {product.billing === "monthly" ? "a month" : "once"}</small>
            </span>
          ) : null}
          {state === "out" ? (
            <Chip kind="quiet">Left out</Chip>
          ) : state === "bad" ? (
            <Chip kind="stop" icon="ban">
              Not billable
            </Chip>
          ) : state === "open" ? (
            <Chip kind="warn" icon="triangle-alert">
              Not in the catalog
            </Chip>
          ) : (
            <Chip kind="ok" icon="check">
              Matched
            </Chip>
          )}
        </div>
      </div>
    </li>
  );
}

/** A catalog row in the pick sheet: Pick when it can be billed, otherwise why not. */
function ProductOption({ product, onPick }: { product: CatalogProductV2; onPick: (p: CatalogProductV2) => void }): React.ReactElement {
  const billable = isBillable(product);
  return (
    <li>
      <div className="row" aria-disabled={billable.ok ? undefined : "true"} data-testid={`mac-pick-option-${product.id}`}>
        <div className="row-main">
          <span className={billable.ok ? "row-t" : "row-t lite muted"}>{product.name}</span>
          <span className="row-m">{[product.category, product.billing ? BILLING[product.billing] : null, formatCents(product.listPrice)].filter(Boolean).join(" \u00b7 ")}</span>
        </div>
        <div className="row-end">
          {billable.ok ? (
            <button className="btn small" type="button" data-testid={`mac-pick-${product.id}`} onClick={() => onPick(product)}>
              Pick
            </button>
          ) : (
            <span className="why-off">{billable.reason}</span>
          )}
        </div>
      </div>
    </li>
  );
}

/** Picks the catalog product a charge Omni could not match bills as: the server's suggestions, then a search. */
function PickSheet({
  line,
  ticket,
  catalog,
  catalogError,
  onPick,
  onLeaveOut,
  onClose,
}: {
  line: MacLine;
  ticket: SupportTicket;
  catalog: CatalogProductV2[] | null;
  catalogError: Error | null;
  onPick: (p: CatalogProductV2) => void;
  onLeaveOut: () => void;
  onClose: () => void;
}): React.ReactElement {
  const [pq, setPq] = useState("");
  const c = line.candidate;
  const suggested = c.suggestions.flatMap((s) => {
    const p = catalog?.find((x) => x.id === s.productId);
    return p ? [p] : [];
  });
  const found = catalog ? searchCatalog(catalog, pq, false) : [];
  return (
    <Sheet
      title="Pick a product"
      label={`Pick a product for ${c.chargeName}`}
      meta={`for ${c.chargeName}`}
      onClose={onClose}
      foot={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn" type="button" data-testid="mac-pick-leave-out" onClick={onLeaveOut}>
            Leave it out
          </button>
        </>
      }
    >
      {/* The Sheet is portaled out of .mac-page; this restores the scope its rules match. */}
      <div className="mac-page" data-testid="mac-pick-sheet">
        <Callout icon="ticket">
          <b>{c.chargeName}</b>
          On {ticket.ticketNumber}: qty {c.qty}, {formatCents(c.chargeUnitPriceCents)} each. Omni found no catalog product for it.
        </Callout>
        <h3>Suggestions</h3>
        {suggested.length ? (
          <ul className="rows" data-testid="mac-pick-suggestions">
            {suggested.map((p) => (
              <ProductOption key={p.id} product={p} onPick={onPick} />
            ))}
          </ul>
        ) : (
          <div className="empty" data-testid="mac-pick-no-suggestions">
            No close matches in the catalog.
          </div>
        )}
        <h3>Search the catalog</h3>
        <label className="search">
          <Icon name="search" size={15} />
          <span className="sr">Search the catalog</span>
          <input className="input" data-testid="mac-pick-q" autoComplete="off" autoFocus placeholder="Product or service name" value={pq} onChange={(e) => setPq(e.target.value)} />
        </label>
        {pq.trim() ? (
          <div className="res" data-testid="mac-pick-results">
            {catalogError ? (
              <div className="empty">{catalogError.message}</div>
            ) : !catalog ? (
              <div className="empty">Loading the catalog.</div>
            ) : !found.length ? (
              <div className="empty">Nothing in the catalog matches &ldquo;{pq.trim()}&rdquo;.</div>
            ) : (
              <ul className="rows">
                {found.slice(0, PICK_ROWS).map((p) => (
                  <ProductOption key={p.id} product={p} onPick={onPick} />
                ))}
              </ul>
            )}
            {found.length > PICK_ROWS ? <div className="res-foot">{found.length - PICK_ROWS} more. Keep typing to narrow it.</div> : null}
          </div>
        ) : null}
      </div>
    </Sheet>
  );
}

/** The last look before Mark sold: no customer signature, and billing approval comes next. */
function ConfirmSold({
  customerName,
  account,
  ticketNumber,
  kept,
  services,
  term,
  today,
  monthly,
  oneTime,
  busy,
  onConfirm,
  onClose,
}: {
  customerName: string;
  account: string;
  ticketNumber: string;
  kept: number;
  services: number;
  term: string;
  today: string;
  monthly: string;
  oneTime: string;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}): React.ReactElement {
  const confirm = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    confirm.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);
  return (
    <>
      <div className="scrim show" onClick={busy ? undefined : onClose} />
      <div className="mac-dialog" role="dialog" aria-modal="true" aria-labelledby="mac-dlg-t" data-testid="mac-confirm">
        <div className="dlg-h">
          <h2 id="mac-dlg-t">Mark sold</h2>
          <button className="iconbtn" type="button" aria-label="Close" disabled={busy} onClick={onClose}>
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="dlg-b">
          <p className="lead">No customer signature. Billing approval comes next.</p>
          <dl className="facts" data-testid="mac-confirm-facts">
            <div>
              <dt>Customer</dt>
              <dd>{customerName}</dd>
            </div>
            <div>
              <dt>OneBill account</dt>
              <dd>{account}</dd>
            </div>
            <div>
              <dt>Lines</dt>
              <dd>
                {kept} from the ticket, {plural(services, "service")} added
              </dd>
            </div>
            <div>
              <dt>Term</dt>
              <dd>
                {term}, starts {today}
              </dd>
            </div>
            <div>
              <dt>Monthly</dt>
              <dd>{monthly}</dd>
            </div>
            <div>
              <dt>One-time</dt>
              <dd>{oneTime}</dd>
            </div>
          </dl>
          <p className="dlg-note">
            Omni puts the MAC order PDF and a note on {ticketNumber} and emails Phoneware. No new ticket and no new charges.
          </p>
        </div>
        <div className="dlg-f">
          <button className="btn" type="button" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button ref={confirm} className="btn primary" type="button" data-testid="mac-confirm-sold" disabled={busy} onClick={onConfirm}>
            <Icon name="badge-check" size={15} />
            {busy ? "Marking it sold" : "Mark sold"}
          </button>
        </div>
      </div>
    </>
  );
}
